import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { CarouselIndexDirective } from '@directives/carousel-index.directive';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { ExaminationActionsService } from '@modules/examination/services/examination-actions.service';
import { FormDisplayComponent } from '@modules/forms/components/form-display/form-display.component';
import { ExaminationForm } from '@pixacare/domain';
import { ActionButton } from '@shared/models/menu-actions/action-buttons';
import { MenuActionId } from '@shared/models/menu-actions/menu-action-id.enum';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { ErrorPipe } from '@shared/pipes/error.pipe';
import { mapToArray } from '@shared/rx-pipes/map-to-array.pipe';
import { TuiCarousel, TuiScrollable, TuiScrollbar } from '@taiga-ui/core';
import { TuiFade } from '@taiga-ui/kit';
import { TuiBlockStatus, TuiHeader } from '@taiga-ui/layout';
import { ExaminationsNavigationService } from '../../services/examination-navigation.service';
import { ExaminationsService } from '../../services/examinations.service';
import { ExaminationActionsComponent } from '../examination-actions/examination-actions.component';
import { ExaminationInfoComponent } from '../examination-info/examination-info.component';
import { ExaminationSelectorComponent } from '../examination-selector/examination-selector.component';
import { ExaminationsNavigationComponent } from '../examinations-navigation/examinations-navigation.component';
import { ExaminationsSkeletonComponent } from '../examinations-skeleton/examinations-skeleton.component';

@Component({
  selector: 'pxc-examination-content',
  imports: [
    CommonModule,
    FormDisplayComponent,
    CarouselIndexDirective,
    TuiCarousel,
    TuiHeader,
    TuiBlockStatus,
    TuiScrollbar,
    TuiScrollable,
    TuiFade,
    EmptyStateComponent,
    ExaminationsNavigationComponent,
    ExaminationSelectorComponent,
    ExaminationActionsComponent,
    ExaminationInfoComponent,
    ExaminationsSkeletonComponent,
    ErrorPipe,
    TranslocoDirective,
  ],
  providers: [ExaminationsNavigationService, ExaminationActionsService],
  templateUrl: './examination-content.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex flex-col',
  },
})
export class ExaminationContentComponent {
  private readonly translocoService = inject(TranslocoService);
  readonly examinationService = inject(ExaminationsService);
  readonly examinationNavigationService = inject(ExaminationsNavigationService);

  readonly isInDialog = input(false);

  protected readonly navigationClass = computed(() =>
    this.isInDialog() ? 'border-t border-border-secondary' : 'bg-background-primary-alt',
  );

  protected readonly scrollableClass = computed(() =>
    this.isInDialog()
      ? 'max-h-[min(62vh,calc(100dvh_-_15rem))] min-h-[min(24rem,calc(100dvh_-_15rem))]'
      : 'max-h-[62vh] min-h-96',
  );

  readonly canWriteExamination$ = this.examinationService.canWriteExamination$;

  readonly examinationForms$ = this.examinationService.examinationForms$.pipe(mapToArray());

  readonly selectedExaminationForm$ = this.examinationNavigationService.examinationForm$;

  readonly state$ = this.examinationService.examinationsState$;

  readonly examinations$ = this.examinationNavigationService.examinations$;

  readonly selectedExamination$ = this.examinationNavigationService.selectedExamination$;

  readonly index$ = this.examinationNavigationService.index$;

  readonly emptyStateActionButtons: ActionButton[] = [
    {
      appearance: 'outline',
      actionIds: [MenuActionId.EXAMINATION_CREATE],
      title: this.translocoService.translate('Ajouter un examen'),
    },
  ];

  selectExaminationForm(examinationForm: ExaminationForm): void {
    if (!examinationForm) {
      return;
    }
    this.examinationNavigationService.selectExaminationForm(examinationForm.id);
  }
}
